import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, SlidersHorizontal } from 'lucide-react';
import FilterSelect from '../filters/FilterSelect';

interface AnimatronicSelectorProps {
  onSelect: (animatronic: any) => void;
}

const AnimatronicSelector: React.FC<AnimatronicSelectorProps> = ({ onSelect }) => {
  const [search, setSearch] = useState('');
  const [animatronics, setAnimatronics] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    country: '',
    park: '',
    type: '',
    status: '',
    year: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    countries: [] as string[],
    parks: [] as string[],
    types: [] as string[],
    statuses: [] as string[],
    years: [] as string[]
  });

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const { data, error } = await supabase
          .from('animatronics')
          .select('country, park, type, status, year')
          .eq('published', true);

        if (error) throw error;

        const options = {
          countries: [...new Set(data.map(a => a.country))].sort(),
          parks: [...new Set(data.map(a => a.park))].sort(),
          types: [...new Set(data.map(a => a.type))].sort(),
          statuses: [...new Set(data.map(a => a.status))].sort(),
          years: [...new Set(data.map(a => a.year.toString()))].sort((a, b) => b.localeCompare(a))
        };

        setFilterOptions(options);
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };

    fetchFilterOptions();
  }, []);

  // Fetch animatronics with filters
  useEffect(() => {
    const fetchAnimatronics = async () => {
      try {
        setIsLoading(true);
        let query = supabase
          .from('animatronics')
          .select('id, name, park, attraction, type, status, country, year, media')
          .eq('published', true);

        // Apply search filter
        if (search) {
          query = query.ilike('name', `%${search}%`);
        }

        // Apply other filters
        if (filters.country) query = query.eq('country', filters.country);
        if (filters.park) query = query.eq('park', filters.park);
        if (filters.type) query = query.eq('type', filters.type);
        if (filters.status) query = query.eq('status', filters.status);
        if (filters.year) query = query.eq('year', parseInt(filters.year));

        // Get results
        const { data, error } = await query.order('name').limit(20);

        if (error) throw error;
        setAnimatronics(data || []);
      } catch (err) {
        console.error('Error fetching animatronics:', err);
        setError(err instanceof Error ? err.message : 'Failed to load animatronics');
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(fetchAnimatronics, 300);
    return () => clearTimeout(timer);
  }, [search, filters]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-content-secondary" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search animatronics..."
            className="w-full pl-10 pr-4 py-2 bg-background border border-gray-800 rounded-lg"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-3 py-2 rounded-lg border transition-colors ${
            showFilters 
              ? 'border-brand text-brand' 
              : 'border-gray-800 text-content-secondary hover:text-content-primary'
          }`}
        >
          <SlidersHorizontal className="w-5 h-5" />
        </button>
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 bg-background-secondary rounded-lg border border-gray-800">
          <div>
            <label className="block text-sm font-medium mb-1">Country</label>
            <FilterSelect
              value={filters.country}
              onChange={(value) => handleFilterChange('country', value)}
              options={[
                { value: '', label: 'All Countries' },
                ...filterOptions.countries.map(c => ({ value: c, label: c }))
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Park</label>
            <FilterSelect
              value={filters.park}
              onChange={(value) => handleFilterChange('park', value)}
              options={[
                { value: '', label: 'All Parks' },
                ...filterOptions.parks.map(p => ({ value: p, label: p }))
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <FilterSelect
              value={filters.type}
              onChange={(value) => handleFilterChange('type', value)}
              options={[
                { value: '', label: 'All Types' },
                ...filterOptions.types.map(t => ({ value: t, label: t }))
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <FilterSelect
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              options={[
                { value: '', label: 'All Statuses' },
                ...filterOptions.statuses.map(s => ({ value: s, label: s }))
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Year</label>
            <FilterSelect
              value={filters.year}
              onChange={(value) => handleFilterChange('year', value)}
              options={[
                { value: '', label: 'All Years' },
                ...filterOptions.years.map(y => ({ value: y, label: y }))
              ]}
            />
          </div>
        </div>
      )}

      {error ? (
        <div className="text-red-500 text-sm">{error}</div>
      ) : isLoading ? (
        <div className="text-content-secondary text-sm">Loading...</div>
      ) : animatronics.length === 0 ? (
        <div className="text-content-secondary text-sm">No animatronics found</div>
      ) : (
        <div className="space-y-2">
          {animatronics.map((animatronic) => (
            <button
              key={animatronic.id}
              onClick={() => onSelect(animatronic)}
              className="w-full text-left p-3 bg-background hover:bg-background-secondary rounded-lg transition-colors"
            >
              <div className="font-medium text-content-primary">
                {animatronic.name}
              </div>
              <div className="flex flex-wrap gap-2 text-sm text-content-secondary">
                <span>{animatronic.park} - {animatronic.attraction}</span>
                <span>•</span>
                <span>{animatronic.country}</span>
                <span>•</span>
                <span>{animatronic.year}</span>
                <span>•</span>
                <span className="capitalize">{animatronic.status}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnimatronicSelector;